// src/design/PexelsSearchModal.jsx
// Modal for searching Pexels images OR videos, multi-selecting, and importing them
import React, { useState, useCallback, useRef, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

export default function PexelsSearchModal({ subcategory, onImported, onClose, initialQuery, professionId, maxSelect, searchMode }) {
  const isVideoMode = searchMode === 'video';
  const [query, setQuery] = useState(initialQuery || '');
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(1);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set()); // pexels_id set
  const [error, setError] = useState(null);
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [hoveredVideo, setHoveredVideo] = useState(null); // pexels_id of hovered video
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    // Auto-search if initialQuery was provided
    if (initialQuery && initialQuery.trim()) {
      doSearch(initialQuery.trim(), 1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = useCallback(async (q, pg = 1, append = false) => {
    if (!q.trim()) return;
    if (append) {
      setLoadingMore(true);
    } else {
      setSearching(true);
    }
    setError(null);

    try {
      // Use different endpoint based on search mode
      const profParam = professionId ? `&profession_id=${professionId}` : '';
      const endpoint = isVideoMode
        ? `${API_URL}/api/designcenter/pexels/videos/search?q=${encodeURIComponent(q.trim())}&page=${pg}&per_page=15${profParam}`
        : `${API_URL}/api/designcenter/pexels/search?q=${encodeURIComponent(q.trim())}&page=${pg}&per_page=30${profParam}`;
      const res = await fetch(endpoint, { credentials: 'include' });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        setError(data.error || 'Search failed');
        return;
      }

      setTotalResults(data.total_results || 0);
      setPage(pg);
      setSearchedOnce(true);

      const items = isVideoMode ? (data.videos || []) : (data.photos || []);

      if (append) {
        setResults((prev) => [...prev, ...items]);
      } else {
        setResults(items);
        setSelected(new Set());
      }
    } catch (err) {
      setError(`Network error searching eHandshake ${isVideoMode ? 'videos' : 'images'}`);
    } finally {
      setSearching(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [isVideoMode, professionId]);

  const handleSearch = () => doSearch(query, 1, false);

  const toggleSelect = (pexelsId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pexelsId)) {
        next.delete(pexelsId);
      } else if (maxSelect === 1) {
        // Single-select mode: replace previous selection
        next.clear();
        next.add(pexelsId);
      } else {
        next.add(pexelsId);
      }
      return next;
    });
  };

  // Infinite scroll handler
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || loadingMoreRef.current || searching) return;

    const hasMore = results.length < totalResults;
    if (!hasMore) return;

    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
    if (nearBottom) {
      loadingMoreRef.current = true;
      doSearch(query, page + 1, true);
    }
  }, [results.length, totalResults, searching, query, page, doSearch]);

  const handleImport = async () => {
    if (!selected.size) return;
    setImporting(true);
    setError(null);

    try {
      let importedData = [];
      if (isVideoMode) {
        // Video import
        const allSelected = results
          .filter((v) => selected.has(v.pexels_id))
          .map((v) => ({
            pexels_id: v.pexels_id,
            video_url: v.video_url,
            preview_image: v.preview_image,
            user_name: v.user_name,
            width: v.width,
            height: v.height,
          }));

        const res = await fetch(`${API_URL}/api/designcenter/pexels/videos/import`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ videos: allSelected, subcategory }),
        });
        const data = await res.json();
        if (!res.ok || !data.ok) {
          setError(data.error || 'Video import failed');
          setImporting(false);
          return;
        }
      } else {
        // Image import (existing logic)
        const allSelected = results
          .filter((p) => selected.has(p.pexels_id))
          .map((p) => ({
            pexels_id: p.pexels_id,
            src_large: p.src_large,
            src_original: p.src_original,
            alt: p.alt,
            photographer: p.photographer,
            width: p.width,
            height: p.height,
          }));

        const res = await fetch(`${API_URL}/api/designcenter/pexels/import`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ photos: allSelected, subcategory }),
        });
        const data = await res.json();
        if (!res.ok || !data.ok) {
          setError(data.error || 'Import failed');
          setImporting(false);
          return;
        }
        importedData = data.imported || [];
      }

      setImporting(false);
      onClose();
      onImported?.(importedData);
    } catch (err) {
      setError('Network error importing');
      setImporting(false);
    }
  };

  const hasMore = results.length < totalResults;
  const selectedCount = selected.size;
  const mediaLabel = isVideoMode ? 'video' : 'image';
  const mediaLabelPlural = isVideoMode ? 'videos' : 'images';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl flex flex-col w-[680px] max-w-[92vw] h-[80vh] overflow-hidden">
        {/* Header — search bar */}
        <div className="shrink-0 px-4 pt-4 pb-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                placeholder={`Search eHandshake for ${mediaLabelPlural}...`}
                className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:border-[#00aef7] focus:ring-1 focus:ring-[#00aef7]/30"
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching || !query.trim()}
                className="px-4 py-2 rounded-lg bg-[#00aef7] text-white text-sm font-medium hover:bg-[#0097d6] disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 text-lg"
              title="Close"
            >
              &times;
            </button>
          </div>
          {totalResults > 0 && (
            <div className="mt-1.5 text-xs text-gray-500">
              {totalResults.toLocaleString()} results found
              {selectedCount > 0 && (
                <span className="ml-2 text-[#00aef7] font-medium">
                  {selectedCount} selected
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results grid — infinite scroll */}
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex-1 min-h-0 overflow-auto p-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        >
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 mb-3">
              {error}
            </div>
          )}

          {/* Empty state — no results yet */}
          {!results.length && !searching && !searchedOnce && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
              </svg>
              <span className="text-sm">Search for {mediaLabelPlural} to add to your library</span>
            </div>
          )}

          {/* No results found after search (video mode: show Generate AI Video button) */}
          {!results.length && !searching && searchedOnce && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              {isVideoMode ? (
                <>
                  <svg className="w-12 h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                  <span className="text-sm mb-4">No videos found for this search</span>
                  <button
                    type="button"
                    onClick={() => {
                      // Phase 2 placeholder — for now just alert
                      alert('AI Video generation coming soon! This feature is under development.');
                    }}
                    className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-sm font-semibold hover:from-purple-600 hover:to-indigo-700 transition shadow-md flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
                    </svg>
                    Generate an AI Video
                  </button>
                </>
              ) : (
                <>
                  <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                  </svg>
                  <span className="text-sm">No images found — try a different search</span>
                </>
              )}
            </div>
          )}

          {results.length > 0 && (
            <div className={`grid ${isVideoMode ? 'grid-cols-2' : 'grid-cols-3'} gap-2`}>
              {results.map((item) => {
                const isSelected = selected.has(item.pexels_id);
                const isImported = item.already_imported;

                return (
                  <button
                    key={item.pexels_id}
                    type="button"
                    onClick={() => toggleSelect(item.pexels_id)}
                    className={`relative rounded-lg overflow-hidden border-2 transition group ${
                      isSelected
                        ? isImported
                          ? 'border-green-500 ring-2 ring-green-400/30'
                          : 'border-[#00aef7] ring-2 ring-[#00aef7]/30'
                        : isImported
                        ? 'border-green-300/50 hover:border-green-400'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                    title={
                      isImported
                        ? isSelected
                          ? 'Selected — will move to top of your library'
                          : 'Already in library — click to boost to top'
                        : isVideoMode
                        ? `Video (${item.duration || 0}s)`
                        : item.alt || 'Click to select'
                    }
                  >
                    <div
                      className={`${isVideoMode ? 'aspect-video' : 'aspect-[4/3]'} bg-gray-100`}
                      onMouseEnter={() => isVideoMode && setHoveredVideo(item.pexels_id)}
                      onMouseLeave={() => isVideoMode && setHoveredVideo(null)}
                    >
                      {isVideoMode ? (
                        hoveredVideo === item.pexels_id ? (
                          <video
                            src={item.video_url}
                            className="w-full h-full object-cover"
                            autoPlay
                            muted
                            loop
                            playsInline
                          />
                        ) : (
                          <img
                            src={item.preview_image}
                            alt={`Video preview`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        )
                      ) : (
                        <img
                          src={item.src_medium}
                          alt={item.alt || ''}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                    </div>

                    {/* Video duration badge */}
                    {isVideoMode && item.duration && hoveredVideo !== item.pexels_id && (
                      <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-medium">
                        {item.duration}s
                      </div>
                    )}

                    {/* Play icon for videos — hide when hovering (video is playing) */}
                    {isVideoMode && !isSelected && hoveredVideo !== item.pexels_id && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-10 h-10 rounded-full bg-black/40 flex items-center justify-center group-hover:bg-black/60 transition">
                          <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Selection checkmark */}
                    {isSelected && (
                      <div className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center shadow ${
                        isImported ? 'bg-green-500' : 'bg-[#00aef7]'
                      }`}>
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                      </div>
                    )}

                  </button>
                );
              })}
            </div>
          )}

          {/* Loading more indicator */}
          {loadingMore && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#00aef7]" />
              <span className="ml-2 text-xs text-gray-400">Loading more...</span>
            </div>
          )}

          {/* Initial searching indicator */}
          {searching && !results.length && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00aef7]" />
            </div>
          )}
        </div>

        {/* Footer — import button */}
        <div className="shrink-0 border-t border-gray-200 px-4 py-3 flex items-center justify-between bg-white">

          <button
            type="button"
            onClick={handleImport}
            disabled={!selectedCount || importing}
            className={`px-5 py-2 rounded-lg text-white text-center text-sm font-medium ${
              selectedCount && !importing
                ? 'bg-[#00aef7] hover:bg-[#0097d6]'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {importing
              ? 'Importing...'
              : selectedCount
              ? `Add ${selectedCount} ${selectedCount > 1 ? mediaLabelPlural : mediaLabel}`
              : `Select ${mediaLabelPlural} to add`}
          </button>
        </div>
      </div>
    </div>
  );
}
